import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Meter {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
  status: "online" | "offline" | "fault";
  valve_open: boolean;
  latest_reading: { cumulative_value: string } | null;
}

interface TokenBalance {
  meter: number;
  balance: string;
  unit_label: string;
}

interface Property {
  id: number;
  name: string;
  token_value: string;
  unit_label: string;
}

interface WaterToken {
  id: number;
  token_code: string;
  units_allocated: string;
  amount_paid: string;
  status: string;
  created_at: string;
}

// ── Preset amounts ────────────────────────────────────────────────────────────
const PRESETS = [50, 100, 200, 500, 1000, 2000];

// ── Step indicator ────────────────────────────────────────────────────────────
function StepDot({ n, label, active, done }: { n: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold transition-all duration-300 ${
          done
            ? "bg-emerald-500 text-white shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
            : active
            ? "bg-[#0f2557] text-white shadow-[0_0_0_4px_rgba(15,37,87,0.15)]"
            : "bg-gray-100 text-gray-400"
        }`}
      >
        {done ? (
          <svg viewBox="0 0 12 12" fill="none" className="w-3.5 h-3.5">
            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          n
        )}
      </div>
      <span
        className={`text-[11px] font-medium whitespace-nowrap ${
          active ? "text-[#0f2557]" : done ? "text-emerald-600" : "text-gray-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function StepLine({ done }: { done: boolean }) {
  return (
    <div className="flex-1 h-0.5 mx-1 mb-5 rounded-full overflow-hidden bg-gray-100">
      <div
        className={`h-full rounded-full transition-all duration-500 ${done ? "bg-emerald-400 w-full" : "w-0"}`}
      />
    </div>
  );
}

// ── Meter card ────────────────────────────────────────────────────────────────
function MeterCard({
  meter,
  balance,
  selected,
  onSelect,
}: {
  meter: Meter;
  balance: TokenBalance | undefined;
  selected: boolean;
  onSelect: () => void;
}) {
  const bal = balance ? parseFloat(balance.balance) : 0;
  const label = balance?.unit_label ?? "units";
  const pct = Math.min(100, (bal / 200) * 100); // treat 200 as "full"
  const low = bal < 10;
  const critical = bal < 3;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-2xl p-4 transition-all duration-200 border-2 ${
        selected
          ? "border-[#0f2557] bg-[#0f2557]/5 shadow-md"
          : "border-gray-100 bg-white hover:border-sky-200 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[14px] font-bold text-gray-800 leading-tight">{meter.property_name}</p>
          <p className="text-[12px] text-gray-400 mt-0.5">Unit {meter.unit_number}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {/* Online dot */}
          <span className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            meter.status === "online"
              ? "bg-emerald-50 text-emerald-600"
              : meter.status === "fault"
              ? "bg-rose-50 text-rose-500"
              : "bg-gray-100 text-gray-400"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              meter.status === "online" ? "bg-emerald-400 animate-pulse" : "bg-gray-300"
            }`} />
            {meter.status === "online" ? "Online" : meter.status === "fault" ? "Fault" : "Offline"}
          </span>
          {/* Valve */}
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
            meter.valve_open ? "bg-sky-50 text-sky-600" : "bg-red-50 text-red-500"
          }`}>
            {meter.valve_open ? "💧 Flowing" : "🔴 Closed"}
          </span>
        </div>
      </div>

      {/* Balance */}
      <div className="mt-1 mb-2">
        <div className="flex items-end justify-between mb-1.5">
          <span className="text-[11px] text-gray-400 font-medium">Remaining balance</span>
          <span className={`text-[13px] font-bold ${critical ? "text-red-500" : low ? "text-amber-500" : "text-gray-800"}`}>
            {bal.toFixed(2)} <span className="font-normal text-gray-400 text-[11px]">{label}</span>
          </span>
        </div>
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              critical ? "bg-red-400" : low ? "bg-amber-400" : "bg-emerald-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {low && (
          <p className={`text-[11px] mt-1 font-medium ${critical ? "text-red-500" : "text-amber-500"}`}>
            {critical ? "⚠ Critical — top up now!" : "⚠ Low balance — consider topping up"}
          </p>
        )}
      </div>

      {/* Serial */}
      <p className="text-[10.5px] text-gray-300 font-mono mt-2">{meter.serial_number}</p>

      {selected && (
        <div className="mt-2 pt-2 border-t border-[#0f2557]/10 flex items-center gap-1.5">
          <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5 text-[#0f2557]">
            <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth={1.5} />
            <path d="M4.5 7l2 2 3-3" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[11.5px] font-semibold text-[#0f2557]">Selected</span>
        </div>
      )}
    </button>
  );
}

// ── Token receipt display ─────────────────────────────────────────────────────
function TokenReceipt({ token_code, units, amount, unit_label, onDone }: {
  token_code: string;
  units: string;
  amount: string;
  unit_label: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(token_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm p-7 shadow-2xl animate-[slideUp_0.3s_ease]">

        {/* Success icon */}
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center shadow-[0_0_0_8px_rgba(16,185,129,0.1)]">
            <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
              <path
                d="M5 13l4 4L19 7"
                stroke="#10b981"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <h2 className="text-center text-[20px] font-bold text-gray-900 mb-1">Payment Successful!</h2>
        <p className="text-center text-[13px] text-gray-400 mb-6">
          Your token has been generated. Water will start flowing shortly.
        </p>

        {/* Token code */}
        <div className="bg-gradient-to-br from-[#0f2557] to-[#1e3a8a] rounded-2xl p-5 mb-4 text-center shadow-lg">
          <p className="text-sky-300 text-[11px] font-semibold uppercase tracking-widest mb-3">Water Token</p>
          <div className="flex items-center justify-center gap-2 mb-4">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 flex-shrink-0">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#38bdf8" opacity=".9" />
            </svg>
            <code className="text-white text-[20px] font-bold tracking-[0.15em] font-mono leading-tight">
              {token_code}
            </code>
          </div>
          <button
            onClick={copy}
            className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all ${
              copied
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-white/10 text-sky-200 hover:bg-white/20"
            }`}
          >
            {copied ? (
              <>
                <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
                  <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
                  <rect x="1.5" y="4.5" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth={1.3} />
                  <path d="M4.5 4.5V3A1.5 1.5 0 0 1 6 1.5h5A1.5 1.5 0 0 1 12.5 3v7A1.5 1.5 0 0 1 11 11.5H9.5" stroke="currentColor" strokeWidth={1.3} />
                </svg>
                Copy token
              </>
            )}
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-sky-50 rounded-xl p-3 text-center">
            <p className="text-[11px] text-sky-500 font-medium mb-0.5">Units Allocated</p>
            <p className="text-[18px] font-bold text-sky-700">{parseFloat(units).toFixed(2)}</p>
            <p className="text-[10.5px] text-sky-400">{unit_label}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-[11px] text-emerald-500 font-medium mb-0.5">Amount Paid</p>
            <p className="text-[18px] font-bold text-emerald-700">KES {parseFloat(amount).toLocaleString()}</p>
            <p className="text-[10.5px] text-emerald-400">via M-Pesa</p>
          </div>
        </div>

        <p className="text-center text-[12px] text-gray-400 mb-5">
          A confirmation SMS has been sent to your phone.
        </p>

        <button
          onClick={onDone}
          className="w-full bg-[#0f2557] hover:bg-[#0e1f4a] text-white font-bold py-3.5 rounded-xl transition-colors text-[14px]"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── STK waiting screen ────────────────────────────────────────────────────────
function StkWaiting({ phone, onCancel }: { phone: string; onCancel: () => void }) {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? "." : d + ".")), 600);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl text-center">
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
            <svg viewBox="0 0 40 40" fill="none" className="w-10 h-10">
              <rect width="40" height="40" rx="20" fill="#dcfce7" />
              <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fontSize="18">📱</text>
            </svg>
          </div>
        </div>
        <h3 className="text-[18px] font-bold text-gray-900 mb-2">Check your phone{dots}</h3>
        <p className="text-[13px] text-gray-500 mb-1">
          An M-Pesa STK push has been sent to
        </p>
        <p className="text-[15px] font-bold text-gray-800 mb-5">{phone}</p>
        <div className="flex justify-center mb-6">
          <div className="flex gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-green-400"
                style={{ animation: `bounce 1s ease ${i * 0.15}s infinite` }}
              />
            ))}
          </div>
        </div>
        <p className="text-[12px] text-gray-400 mb-5">
          Enter your M-Pesa PIN to complete the payment. This prompt will expire in 2 minutes.
        </p>
        <button
          onClick={onCancel}
          className="text-[13px] text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BuyTokensPage() {
  const { user } = useAuthStore();
  const [meters, setMeters] = useState<Meter[]>([]);
  const [balances, setBalances] = useState<TokenBalance[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [recentTokens, setRecentTokens] = useState<WaterToken[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  // form state
  const [selectedMeterId, setSelectedMeterId] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState(user?.phone_number ?? "");
  const [customAmount, setCustomAmount] = useState(false);

  // submission state
  const [submitting, setSubmitting] = useState(false);
  const [stkSent, setStkSent] = useState(false);
  const [error, setError] = useState("");
  const [successToken, setSuccessToken] = useState<{
    code: string; units: string; amount: string; unit_label: string;
  } | null>(null);

  const phoneRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    Promise.all([
      api.get("/meters/"),
      api.get("/tokens/balances/"),
      api.get("/properties/"),
      api.get("/tokens/water-tokens/"),
    ]).then(([m, b, pr, wt]) => {
      const mList: Meter[] = m.data.results ?? m.data;
      setMeters(mList);
      setBalances(b.data.results ?? b.data);
      setProperties(pr.data.results ?? pr.data);
      setRecentTokens((wt.data.results ?? wt.data).slice(0, 5));
      if (mList.length === 1) setSelectedMeterId(mList[0].id);
    }).finally(() => setPageLoading(false));
  }, []);

  // Derived
  const selectedMeter = meters.find((m) => m.id === selectedMeterId);
  const selectedBalance = balances.find((b) => b.meter === selectedMeterId);
  const selectedProperty = selectedMeter
    ? properties.find((p) => p.name === selectedMeter.property_name)
    : null;
  const tokenValue = selectedProperty ? parseFloat(selectedProperty.token_value) : null;
  const unitLabel = selectedBalance?.unit_label ?? selectedProperty?.unit_label ?? "units";
  const estimatedUnits =
    tokenValue && amount ? (parseFloat(amount) / tokenValue).toFixed(2) : null;

  // Step tracking
  const step1Done = !!selectedMeterId;
  const step2Done = step1Done && !!amount && parseFloat(amount) >= 1;
  const step3Done = step2Done && !!phone;

  const handlePreset = (val: number) => {
    setAmount(String(val));
    setCustomAmount(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMeterId || !amount || !phone) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/payments/initiate/", {
        meter_id: selectedMeterId,
        amount: parseFloat(amount),
        phone_number: phone,
      });
      setStkSent(true);
    } catch (err: any) {
      const d = err.response?.data;
      setError(d?.detail || JSON.stringify(d) || "Payment initiation failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAmount("");
    setCustomAmount(false);
    setStkSent(false);
    setSuccessToken(null);
    setError("");
  };

  if (pageLoading) {
    return (
      <div className="space-y-4 pt-4">
        <div className="h-12 rounded-2xl bg-gray-100 animate-pulse w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-56 rounded-2xl bg-gray-100 animate-pulse" />
          <div className="h-56 rounded-2xl bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>

      {/* STK waiting overlay */}
      {stkSent && !successToken && (
        <StkWaiting phone={phone} onCancel={() => setStkSent(false)} />
      )}

      {/* Success overlay */}
      {successToken && (
        <TokenReceipt
          token_code={successToken.code}
          units={successToken.units}
          amount={successToken.amount}
          unit_label={successToken.unit_label}
          onDone={resetForm}
        />
      )}

      <div className="pb-10">

        {/* ── Page header ── */}
        <div className="mb-6">
          <h1 className="text-[22px] font-bold text-gray-900">Buy Water Tokens</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            Pay via M-Pesa and water will flow automatically
          </p>
        </div>

        {/* ── Step indicator ── */}
        <div className="flex items-center mb-7 max-w-sm">
          <StepDot n={1} label="Select meter" active={!step1Done} done={step1Done} />
          <StepLine done={step1Done} />
          <StepDot n={2} label="Set amount" active={step1Done && !step2Done} done={step2Done} />
          <StepLine done={step2Done} />
          <StepDot n={3} label="Pay" active={step2Done} done={false} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── Step 1: Meter selection — full width ── */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-6 h-6 rounded-full bg-[#0f2557] flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">1</div>
              <h2 className="text-[15px] font-bold text-gray-800">Choose your meter</h2>
            </div>

            {meters.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-[13px]">
                No meters found for your account.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {meters.map((m) => (
                  <MeterCard
                    key={m.id}
                    meter={m}
                    balance={balances.find((b) => b.meter === m.id)}
                    selected={selectedMeterId === m.id}
                    onSelect={() => setSelectedMeterId(m.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Steps 2 & 3 side by side on large screens ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* ── Step 2: Amount ── */}
          <div className={`bg-white rounded-2xl shadow-sm p-5 transition-opacity duration-300 ${!step1Done ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${step1Done ? "bg-[#0f2557] text-white" : "bg-gray-100 text-gray-400"}`}>2</div>
              <h2 className="text-[15px] font-bold text-gray-800">How much to top up?</h2>
            </div>

            {/* Quick presets */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handlePreset(p)}
                  className={`py-3 rounded-xl text-[13px] font-semibold transition-all duration-150 ${
                    amount === String(p) && !customAmount
                      ? "bg-[#0f2557] text-white shadow-md"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100"
                  }`}
                >
                  KES {p >= 1000 ? `${p / 1000}k` : p}
                </button>
              ))}
            </div>

            {/* Custom amount toggle */}
            <button
              type="button"
              onClick={() => { setCustomAmount(true); setAmount(""); }}
              className={`w-full py-2.5 rounded-xl text-[13px] font-semibold border-2 border-dashed transition-all mb-4 ${
                customAmount
                  ? "border-[#0f2557] text-[#0f2557] bg-[#0f2557]/5"
                  : "border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500"
              }`}
            >
              {customAmount ? "Custom amount" : "+ Enter custom amount"}
            </button>

            {customAmount && (
              <div className="relative mb-4">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[14px] font-bold text-gray-400">KES</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  placeholder="Enter amount"
                  className="w-full pl-14 pr-4 py-3.5 border-2 border-[#0f2557]/20 rounded-xl text-[15px] font-bold text-gray-800 focus:outline-none focus:border-[#0f2557] bg-[#0f2557]/3 placeholder:font-normal placeholder:text-gray-300"
                />
              </div>
            )}

            {/* Token calculator */}
            {amount && parseFloat(amount) >= 1 ? (
              <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl p-4 flex items-center gap-4 border border-sky-100">
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
                    <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#38bdf8" opacity=".9" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[12px] text-sky-500 font-medium">You will receive approximately</p>
                  <p className="text-[22px] font-bold text-sky-700 leading-tight">
                    {estimatedUnits ?? "—"}{" "}
                    <span className="text-[13px] font-medium text-sky-500">{unitLabel}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-sky-400">Rate</p>
                  <p className="text-[13px] font-bold text-sky-600">
                    KES {tokenValue ?? "—"}<span className="font-normal text-sky-400">/{unitLabel.replace(/s$/, "")}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-gray-100 p-5 text-center">
                <p className="text-[13px] text-gray-300">Select an amount to see your token estimate</p>
              </div>
            )}
          </div>

          {/* ── Step 3: Phone & Pay ── */}
          <div className={`bg-white rounded-2xl shadow-sm p-5 transition-opacity duration-300 ${!step2Done ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${step2Done ? "bg-[#0f2557] text-white" : "bg-gray-100 text-gray-400"}`}>3</div>
              <h2 className="text-[15px] font-bold text-gray-800">Pay with M-Pesa</h2>
            </div>

            {/* M-Pesa branding strip */}
            <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4">
                  <path d="M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2z" fill="white" opacity=".3" />
                  <path d="M7 10l2 2 4-4" stroke="white" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <p className="text-[12.5px] font-bold text-green-800">Secure M-Pesa Payment</p>
                <p className="text-[11px] text-green-600">You'll get an STK push — enter your PIN to confirm</p>
              </div>
            </div>

            <label className="block text-[13px] font-semibold text-gray-600 mb-2">M-Pesa phone number</label>
            <div className="relative mb-1">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                <span className="text-[14px]">🇰🇪</span>
                <span className="text-[13px] text-gray-400 font-medium">+254</span>
                <div className="w-px h-4 bg-gray-200" />
              </div>
              <input
                ref={phoneRef}
                type="tel"
                value={phone.startsWith("254") ? phone.slice(3) : phone.startsWith("0") ? phone.slice(1) : phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 9);
                  setPhone("254" + v);
                }}
                placeholder="712 345 678"
                className="w-full pl-28 pr-4 py-3.5 border-2 border-gray-100 rounded-xl text-[14px] font-medium text-gray-800 focus:outline-none focus:border-[#0f2557] placeholder:text-gray-300 bg-gray-50"
              />
            </div>
            <p className="text-[11.5px] text-gray-400 mb-5">
              An M-Pesa prompt will be sent to this number. Ensure it matches your registered Safaricom line.
            </p>

            {/* Error */}
            {error && (
              <div className="mb-4 flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl">
                <svg viewBox="0 0 18 18" fill="none" className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5">
                  <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth={1.5} />
                  <path d="M9 5v5M9 13v.5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
                </svg>
                <p className="text-[12.5px] text-red-600 font-medium">{error}</p>
              </div>
            )}

            {/* Order summary */}
            {step3Done && (
              <div className="mb-4 rounded-xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
                {[
                  { label: "Meter", value: `${selectedMeter?.property_name} · Unit ${selectedMeter?.unit_number}` },
                  { label: "Amount", value: `KES ${parseFloat(amount).toLocaleString()}` },
                  { label: "Estimated units", value: estimatedUnits ? `${estimatedUnits} ${unitLabel}` : "—" },
                  { label: "Phone", value: `+254 ${phone.startsWith("254") ? phone.slice(3) : phone}` },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center px-4 py-2.5">
                    <span className="text-[12.5px] text-gray-400">{row.label}</span>
                    <span className="text-[12.5px] font-semibold text-gray-700">{row.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!step3Done || submitting}
              className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-[15px] transition-all duration-200 ${
                step3Done && !submitting
                  ? "bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white shadow-lg shadow-green-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
                  </svg>
                  Sending prompt…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 22 22" fill="none" className="w-5 h-5">
                    <rect width="22" height="22" rx="11" fill="rgba(255,255,255,0.25)" />
                    <path d="M6 11h10M13 8l3 3-3 3" stroke="white" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Pay KES {amount ? parseFloat(amount).toLocaleString() : "—"} via M-Pesa
                </>
              )}
            </button>
          </div>

          {/* close Steps 2 & 3 grid */}
          </div>

        </form>

        {/* ── Recent tokens ── */}
        {recentTokens.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm p-5">
            <h2 className="text-[15px] font-bold text-gray-800 mb-4">Recent Tokens</h2>
            <div className="space-y-2.5">
              {recentTokens.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-[#0f2557]/8 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" className="w-4.5 h-4.5">
                      <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#0ea5e9" opacity=".8" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <code className="text-[12.5px] font-mono font-bold text-gray-700">{t.token_code}</code>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {parseFloat(t.units_allocated).toFixed(2)} {unitLabel} · KES {parseFloat(t.amount_paid).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                      t.status === "active"
                        ? "bg-sky-50 text-sky-600"
                        : t.status === "consumed"
                        ? "bg-gray-100 text-gray-400"
                        : "bg-red-50 text-red-500"
                    }`}>
                      {t.status}
                    </span>
                    <p className="text-[10.5px] text-gray-400 mt-1">
                      {new Date(t.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
